import type { Variants } from 'framer-motion';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { type PropsWithChildren } from 'react';

interface BulkJobsBadgeProps extends PropsWithChildren {
  /** Number of non-suppressed in-flight (queued/running) bulk jobs. */
  activeCount: number;
  /** Number of completed bulk jobs the user hasn't viewed yet. */
  unviewedCount: number;
  /** Whether any job in the recent window has failed. Shifts pill to critical. */
  hasFailure: boolean;
}

/**
 * BulkJobsBadge — wraps the BoltIcon button with an amber processing ring
 * and a completion count badge.
 *
 * UX divergence #1: uses `border-t-chariot-status-warning` (amber) for the
 * processing ring, NOT brand-primary, to visually distinguish bulk jobs
 * from the adjacent GlobalDownload exports ring.
 *
 * Mirrors DownloadIconBadge composition.
 */
export function BulkJobsBadge({
  children,
  activeCount,
  unviewedCount,
  hasFailure,
}: BulkJobsBadgeProps) {
  const shouldReduceMotion = useReducedMotion();

  const isProcessing = activeCount > 0;
  const hasUnviewed = unviewedCount > 0;

  const badgeVariants: Variants = shouldReduceMotion
    ? {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
      }
    : {
        initial: { scale: 0, opacity: 0 },
        animate: {
          scale: 1,
          opacity: 1,
          transition: { type: 'spring', stiffness: 500, damping: 25 },
        },
        exit: { scale: 0, opacity: 0, transition: { duration: 0.15 } },
      };

  return (
    <div className="relative inline-flex items-center justify-center">
      {/* Amber processing ring (UX divergence #1 — amber, not brand-primary) */}
      {isProcessing && (
        <span
          role="status"
          aria-label={`${activeCount} bulk action${activeCount === 1 ? '' : 's'} in progress`}
          className={`border-t-chariot-status-warning border-chariot-status-warning/20 pointer-events-none absolute inset-0 rounded-full border-2 ${
            shouldReduceMotion ? '' : 'animate-spin'
          }`}
          style={{ borderRadius: '50%' }}
        />
      )}

      {children}

      {/* Unviewed count badge */}
      <AnimatePresence>
        {hasUnviewed && (
          <motion.span
            key="bulk-unviewed-badge"
            variants={badgeVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className={`absolute -top-1.5 -right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full px-0.5 text-[10px] font-semibold text-white ${
              hasFailure
                ? 'bg-chariot-status-critical'
                : 'bg-chariot-brand-primary'
            }`}
            aria-label={`${unviewedCount} completed bulk action${unviewedCount === 1 ? '' : 's'}`}
          >
            {unviewedCount > 9 ? '9+' : unviewedCount}
          </motion.span>
        )}
      </AnimatePresence>
    </div>
  );
}
